import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Room, createLocalTracks } from 'livekit-client'
import { message } from 'antd'

const LiveKitRoomAudioOnly = ({
  connectStatus,
  setConnectedToLiveKit,
  setIsSpeaking,
  setCoachStatus,
  token,
  serverUrl
}) => {
  const [participants, setParticipants] = useState([])
  const [speakingMap, setSpeakingMap] = useState({})
  const [localSpeaking, setLocalSpeaking] = useState(false)
  const [connected, setConnected] = useState(false)
  const [audioEnabled, setAudioEnabled] = useState(false)
  const [needsUserInteraction, setNeedsUserInteraction] = useState(false)
  const roomRef = useRef(null)
  const localTracksRef = useRef([])
  const audioElementsRef = useRef(new Map())
  // Initialize audio context and enable audio playback
  const enableAudio = async () => {
    try {
      // Resume audio context if suspended
      if (
        typeof AudioContext !== 'undefined' ||
        typeof window.webkitAudioContext !== 'undefined'
      ) {
        const AudioContextClass = AudioContext || window.webkitAudioContext
        const audioContext = new AudioContextClass()

        if (audioContext.state === 'suspended') {
          await audioContext.resume()
          console.log('Audio context resumed')
        }
      }

      // Play all existing audio elements
      const audioElements = document.querySelectorAll(
        'audio[data-livekit-audio]'
      )
      const playPromises = Array.from(audioElements).map(el => {
        el.muted = false
        el.volume = 1.0
        return el.play().catch(e => {
          console.warn('Failed to play audio element:', e)
          return null
        })
      })

      await Promise.allSettled(playPromises)
      setAudioEnabled(true)
      setNeedsUserInteraction(false)
      console.log('Audio enabled successfully')
    } catch (error) {
      console.error('Failed to enable audio:', error)
    }
  }

  useEffect(() => {
    if (!connectStatus) return
    setConnectedToLiveKit('connecting')

    const room = new Room()
    roomRef.current = room

    room.on('participantConnected', handleParticipantConnected)
    room.on('participantDisconnected', handleParticipantDisconnected)

    const connectToRoom = async () => {
      try {
        // Create audio container
        let audioContainer = document.getElementById('livekit-audio-container')
        if (!audioContainer) {
          audioContainer = document.createElement('div')
          audioContainer.id = 'livekit-audio-container'
          audioContainer.style.display = 'none' // Hide the container
          document.body.appendChild(audioContainer)
        }

        room.on('trackSubscribed', (track, publication, participant) => {
          console.log(
            'Track subscribed:',
            track.kind,
            'from',
            participant.identity
          )

          if (track.kind === 'audio') {
            const el = track.attach()
            el.setAttribute('data-livekit-audio', 'true')
            el.setAttribute('data-participant-id', participant.sid)
            el.autoplay = true
            el.controls = false
            el.volume = 1.0
            el.muted = false

            // Store reference for cleanup
            audioElementsRef.current.set(participant.sid, el)

            // Add to container
            audioContainer.appendChild(el)

            // Try to play immediately
            const attemptPlay = async () => {
              try {
                await el.play()
                console.log(
                  'Audio playing for participant:',
                  participant.identity
                )
                setAudioEnabled(true)
              } catch (e) {
                console.warn(
                  'Auto-play blocked for participant:',
                  participant.identity,
                  e
                )
                setNeedsUserInteraction(true)
              }
            }

            // Attempt play after a short delay
            setTimeout(attemptPlay, 100)

            // Also try on next user interaction if autoplay failed
            const handleUserGesture = () => {
              el.play().catch(console.warn)
              document.removeEventListener('click', handleUserGesture)
              document.removeEventListener('keydown', handleUserGesture)
            }
            
            // Track remote participant speaking
            participant.on('isSpeakingChanged', () => {
              console.log(
                `Participant ${participant.identity} speaking:`,
                participant.isSpeaking
              )
              setSpeakingMap(prev => ({
                ...prev,
                [participant.sid]: participant.isSpeaking
              }))
            })

            document.addEventListener('click', handleUserGesture, {
              once: true
            })
            document.addEventListener('keydown', handleUserGesture, {
              once: true
            })
          }
        })

        room.on('trackUnsubscribed', (track, publication, participant) => {
          console.log(
            'Track unsubscribed:',
            track.kind,
            'from',
            participant.identity
          )

          if (track.kind === 'audio') {
            // Clean up audio element
            const el = audioElementsRef.current.get(participant.sid)
            if (el) {
              el.remove()
              audioElementsRef.current.delete(participant.sid)
            }

            // Also clean up any remaining detached elements
            track.detach().forEach(el => el.remove())
          }
        })

        const localTracks = await createLocalTracks({ audio: true })
        localTracksRef.current = localTracks

        await room.connect(serverUrl, token, { autoSubscribe: true })

        for (let i = 0; i < localTracks.length; i++) {
          await room.localParticipant.publishTrack(localTracks[i])
        }

        // Track local speaking status
        room.localParticipant.on('isSpeakingChanged', () => {
          setLocalSpeaking(room.localParticipant.isSpeaking)
        })

        // Handle existing participants
        const participantMap = room.participants
        if (participantMap && typeof participantMap.values === 'function') {
          const values = Array.from(participantMap.values())
          for (let i = 0; i < values.length; i++) {
            setCoachStatus(true)
            handleParticipantConnected(values[i])
          }
        }

        setConnected(true)
        setConnectedToLiveKit('connected')
        console.log('Connected to LiveKit room successfully')
      } catch (err) {
        console.error('Connection failed:', err)
        message.error('Failed to connect to LiveKit room')
        setConnectedToLiveKit('disconnected')
        cleanup()
      }
    }

    const cleanup = () => {
      const room = roomRef.current

      try {
        // Clean up audio elements
        audioElementsRef.current.forEach((el, participantId) => {
          el.remove()
        })
        audioElementsRef.current.clear()

        // Remove audio container
        const audioContainer = document.getElementById(
          'livekit-audio-container'
        )
        if (audioContainer) {
          audioContainer.remove()
        }

        if (room) {
          const tracksMap =
            room.localParticipant && room.localParticipant.tracks
          if (tracksMap && typeof tracksMap.values === 'function') {
            const pubs = Array.from(tracksMap.values())
            for (let i = 0; i < pubs.length; i++) {
              const pub = pubs[i]
              if (pub && pub.track) {
                pub.track.stop()
                room.localParticipant.unpublishTrack(pub.track)
              }
            }
          }

          room.disconnect()
        }
      } catch (err) {
        console.warn('Error during cleanup:', err)
      }

      roomRef.current = null

      if (Array.isArray(localTracksRef.current)) {
        for (let i = 0; i < localTracksRef.current.length; i++) {
          const track = localTracksRef.current[i]
          if (track && typeof track.stop === 'function') {
            try {
              track.stop()
            } catch (err) {
              console.warn('Failed to stop track:', err)
            }
          }
        }
      }

      localTracksRef.current = []
      setConnected(false)
      setCoachStatus(false)
      setParticipants([])
      setSpeakingMap({})
      setLocalSpeaking(false)
      setAudioEnabled(false)
      setNeedsUserInteraction(false)
      setConnectedToLiveKit('disconnected')
    }

    connectToRoom()

    return () => {
      cleanup()
    }
  }, [connectStatus])

  useEffect(() => {
    const anyRemoteSpeaking = Object.values(speakingMap).some(val => val)
    if (localSpeaking) {
      setIsSpeaking('user')
    } else if (anyRemoteSpeaking) {
      setCoachStatus(true)
      setIsSpeaking('ai')
    } else {
      setIsSpeaking(null)
    }
  }, [localSpeaking, speakingMap, setIsSpeaking])

  const handleParticipantConnected = participant => {
    console.log('Participant Connected:', participant.identity)
    console.log(
      'Participant tracks:',
      participant.tracks ? participant.tracks.size : 0
    )

    // Debug participant tracks
    if (
      participant.tracks &&
      typeof participant.tracks.forEach === 'function'
    ) {
      participant.tracks.forEach((pub, kind) => {
        console.log(
          `Track: ${kind}, subscribed: ${pub.isSubscribed}, kind: ${pub.kind}`
        )
        if (pub.kind === 'audio') {
          console.log('Audio track found:', pub)
        }
      })
    }

    // Track remote participant speaking
    participant.on('isSpeakingChanged', () => {
      console.log(
        `Participant ${participant.identity} speaking:`,
        participant.isSpeaking
      )
      setSpeakingMap(prev => ({
        ...prev,
        [participant.sid]: participant.isSpeaking
      }))
    })
    setParticipants(prev => [...prev, participant])
  }

  const handleParticipantDisconnected = participant => {
    setParticipants(prev => prev.filter(p => p.sid !== participant.sid))

    setSpeakingMap(prev => {
      const updated = { ...prev }
      delete updated[participant.sid]
      return updated
    })

    // Clean up audio element
    const el = audioElementsRef.current.get(participant.sid)
    if (el) {
      el.remove()
      audioElementsRef.current.delete(participant.sid)
    }
  }

  return (
    <div>
      {/* {connected ? (
        <>
          {needsUserInteraction && !audioEnabled && participants.length > 0 && (
            <div
              style={{
                padding: '10px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                marginBottom: '10px'
              }}
            >
              <p style={{ margin: '0 0 10px 0', color: '#856404' }}>
                🔊 Click to enable audio from other participants:
              </p>
              <button
                onClick={enableAudio}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Enable Audio
              </button>
            </div>
          )}

          {audioEnabled && participants.length > 0 && (
            <p
              style={{ color: 'green', fontSize: '12px', margin: '0 0 10px 0' }}
            >
              🔊 Audio enabled - You should hear other participants
            </p>
          )}

          {localSpeaking && (
            <p style={{ color: 'green', fontWeight: 600 }}>
              🎤 You are speaking
            </p>
          )}

          {participants.length < 1 && <p>Waiting for coach...</p>}

          <ul>
            {participants.map(p => (
              <li key={p.sid}>
                {p.identity}{' '}
                {speakingMap[p.sid] && (
                  <span style={{ color: 'green', fontWeight: 'bold' }}>
                    ● Speaking
                  </span>
                )}
              </li>
            ))}
          </ul>

          {process.env.NODE_ENV === 'development' && (
            <div
              style={{
                marginTop: '20px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                fontSize: '12px',
                borderRadius: '4px'
              }}
            >
              <strong>Debug Info:</strong>
              <br />
              Connected: {connected ? 'Yes' : 'No'}
              <br />
              Audio Enabled: {audioEnabled ? 'Yes' : 'No'}
              <br />
              Needs User Interaction: {needsUserInteraction ? 'Yes' : 'No'}
              <br />
              Participants: {participants.length}
              <br />
              Audio Elements: {audioElementsRef.current.size}
            </div>
          )}
        </>
      ) : null} */}
    </div>
  )
}

export default LiveKitRoomAudioOnly
